import { z } from 'zod'

import { firstNameSchema, lastNameSchema, usernameSchema } from 'src/shared/features/users/schemas/user'

export const passwordSchema = z
  .string()
  .min(10, 'Password must be at least 10 characters')
  .regex(/^(?=.*[a-z])/, 'Password must contain at least 1 lowercase letter')
  .regex(/^(?=.*[A-Z])/, 'Password must contain at least 1 uppercase letter')
  .regex(/^(?=.*\d)/, 'Password must contain at least 1 number')
  // .regex(/^(?=.*[^$*.\[\]{}()?!"@#%&/\\,><':;|_~`+=-])/, 'Password must contain at least 1 special character')
  .regex(/^(?=.*[#?!@$%^&*-])/, 'Password must contain at least 1 special character')
  .regex(
    /^[A-Za-z\d^$*.[\]{}()?!"@#%&/\\,><':;|_~`+=-]+$/,
    'Password can only contain letters, numbers, and these special characters: ^ $ * . [ ] { } ( ) ? - " ! @ # % & / \\ , > < \' : ; | _ ~ ` + = (no spaces)',
  )
  .refine((val) => !val.includes(' '), 'Password cannot contain spaces')

export const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: passwordSchema,
})

export const signupSchema = loginSchema
  .extend({
    confirmPassword: passwordSchema,
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  })

export const requestPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
})

export const resetPasswordSchema = z.object({
  code: z.string(),
  newPassword: passwordSchema,
  confirmPassword: passwordSchema,
})

export const updatePasswordSchema = z
  .object({
    oldPassword: z.string().min(1, 'Current password is required'),
    newPassword: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  })

export type LoginFormData = z.infer<typeof loginSchema>
export type SignupFormData = z.infer<typeof signupSchema>
export type RequestPasswordFormData = z.infer<typeof requestPasswordSchema>
export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>
export type UpdatePasswordFormData = z.infer<typeof updatePasswordSchema>

// ------------------------------------------------------------------------------------------------
// Enterprise auth schemas
// ------------------------------------------------------------------------------------------------

export const enterpriseWelcomeSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
})

export const enterpriseLoginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(10, 'Password must be at least 10 characters'),
})

export const enterpriseSignupSchema = z
  .object({
    email: z.string().email('Please enter a valid email address'),
    password: passwordSchema,
    confirmPassword: passwordSchema,
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  })

export const enterpriseCreateAccountSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: passwordSchema,
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  })

export const enterpriseVerifyEmailSchema = z.object({
  code: z.string().regex(/^[0-9]{6}$/, 'Verification code must be 6 digits'),
})

export type EnterpriseWelcomeFormData = z.infer<typeof enterpriseWelcomeSchema>
export type EnterpriseLoginFormData = z.infer<typeof enterpriseLoginSchema>
export type EnterpriseSignupFormData = z.infer<typeof enterpriseSignupSchema>
export type EnterpriseCreateAccountFormData = z.infer<typeof enterpriseCreateAccountSchema>
export type EnterpriseVerifyEmailFormData = z.infer<typeof enterpriseVerifyEmailSchema>

// ------------------------------------------------------------------------------------------------
// Enterprise onboarding schemas
// ------------------------------------------------------------------------------------------------

export type EnterpriseCompanySizeValues =
  | '1-10'
  | '11-50'
  | '51-200'
  | '201-500'
  | '501-1000'
  | '1001-5000'
  | '5001-10000'
  | '10001+'

export type EnterpriseCompanyTypeValues =
  | 'real-estate-brokerage'
  | 'mortgage-lender'
  | 'mortgage-brokerage'
  | 'amc'
  | 'insurance-brokerage'
  | 'title-company'

// TEMP VALUES FOR FOCUS
export type EnterpriseFocusValues =
  | 'Feature #1'
  | 'Feature #2'
  | 'Feature #3'
  | 'Feature #4'
  | 'Feature #5'
  | 'Feature #6'

export const enterpriseCompanySizeMap: Record<EnterpriseCompanySizeValues, { label: string }> = {
  '1-10': { label: '1-10 employees' },
  '11-50': { label: '11-50 employees' },
  '51-200': { label: '51-200 employees' },
  '201-500': { label: '201-500 employees' },
  '501-1000': { label: '501-1,000 employees' },
  '1001-5000': { label: '1,001-5,000 employees' },
  '5001-10000': { label: '5,001-10,000 employees' },
  '10001+': { label: '10,001+ employees' },
}

export const enterpriseCompanyTypeMap: Record<EnterpriseCompanyTypeValues, { label: string }> = {
  'real-estate-brokerage': { label: 'Real Estate Brokerage' },
  'mortgage-lender': { label: 'Mortgage Lender' },
  'mortgage-brokerage': { label: 'Mortgage Brokerage' },
  amc: { label: 'AMC' },
  'insurance-brokerage': { label: 'Insurance Brokerage' },
  'title-company': { label: 'Title Company' },
}

export const enterpriseAboutYouSchema = z.object({
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  username: usernameSchema,
})

export const enterpriseBusinessAddressSchema = z.object({
  streetAddress1: z.string().min(1, 'Street address is required'),
  streetAddress2: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(1, 'State is required'),
  zipCode: z.string().min(5, 'ZIP code must be at least 5 characters'),
  latitude: z.number(),
  longitude: z.number(),
})

const enterpriseCompanySizeSchema = z.enum([
  '1-10',
  '11-50',
  '51-200',
  '201-500',
  '501-1000',
  '1001-5000',
  '5001-10000',
  '10001+',
])

export const enterpriseCompanyTypeSchema = z.enum([
  'real-estate-brokerage',
  'mortgage-lender',
  'mortgage-brokerage',
  'amc',
  'insurance-brokerage',
  'title-company',
])

export const enterpriseBusinessTypeSchema = z.enum(['LLC', 'Corporation', 'Partnership'])
export type EnterpriseBusinessTypeValues = z.infer<typeof enterpriseBusinessTypeSchema>

export const enterpriseBusinessTypeMap: Record<EnterpriseBusinessTypeValues, { label: string }> = {
  LLC: { label: 'LLC' },
  Corporation: { label: 'Corporation' },
  Partnership: { label: 'Partnership' },
}

export const enterpriseCompanyInfoBaseSchema = z.object({
  companyName: z.string().min(1, 'Legal business name is required'),
  displayName: z.string().optional(),
  companySize: enterpriseCompanySizeSchema,
  companyType: enterpriseCompanyTypeSchema,
  businessType: enterpriseBusinessTypeSchema,
  companyWebsite: z.string().url('Please enter a valid website URL (e.g. https://www.example.com)'),
  businessIdentificationNumber: z.string().min(1, 'Business identification number is required'),
  nmlsCompanyIdentifier: z.string().optional(),
})

export const enterpriseCompanyInfoSchema = enterpriseCompanyInfoBaseSchema.superRefine((data, ctx) => {
  if (
    data.companyType === 'mortgage-brokerage' &&
    (!data.nmlsCompanyIdentifier || data.nmlsCompanyIdentifier.trim() === '')
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'NMLS company identifier is required for mortgage brokerages',
      path: ['nmlsCompanyIdentifier'],
    })
  }
})

export const enterpriseFocusSchema = z.object({
  focus: z
    .array(z.enum(['Feature #1', 'Feature #2', 'Feature #3', 'Feature #4', 'Feature #5', 'Feature #6']))
    .max(2, 'You can select up to at most 2 focus options'),
})

export const enterpriseCompanyRoleValuesSchema = z.enum([
  'Director',
  'GM',
  'VP',
  'CEO',
  'CFO',
  'General Counsel',
  'Other',
])
export type EnterpriseCompanyRoleValues = z.infer<typeof enterpriseCompanyRoleValuesSchema>

export const enterpriseCompanyRoleMap: Record<EnterpriseCompanyRoleValues, { label: string }> = {
  Director: { label: 'Director' },
  GM: { label: 'GM' },
  VP: { label: 'VP' },
  CEO: { label: 'CEO' },
  CFO: { label: 'CFO' },
  'General Counsel': { label: 'General Counsel' },
  Other: { label: 'Other' },
}

export const enterpriseCompanyRoleSchema = z.object({
  companyRole: enterpriseCompanyRoleValuesSchema,
  businessTitle: z.string().optional(),
})

export const enterpriseOrgNumbersSchema = z.object({
  consentSms: z.boolean(),
  consentMarketing: z.boolean(),
})

export const enterpriseLeadSourcesSchema = z.object({
  leadSources: z
    .array(z.object({ sourceName: z.string(), sourceTransactionType: z.string().optional() }))
    .min(1, 'Select at least one lead source'),
})

export const enterpriseWorkflowsSchema = z.object({
  enabledWorkflows: z.record(z.string(), z.boolean()),
})

// Used for the API request.
export const enterpriseCreateRequestSchema = enterpriseAboutYouSchema
  .merge(enterpriseCompanyInfoBaseSchema)
  .merge(enterpriseFocusSchema)
  .extend({
    email: z.string().email('Please enter a valid email address'),
  })

export type EnterpriseAboutYouFormData = z.infer<typeof enterpriseAboutYouSchema>
export type EnterpriseCompanyInfoFormData = z.infer<typeof enterpriseCompanyInfoSchema>
export type EnterpriseBusinessAddressFormData = z.infer<typeof enterpriseBusinessAddressSchema>
export type EnterpriseCompanyRoleFormData = z.infer<typeof enterpriseCompanyRoleSchema>
export type EnterpriseOrgNumbersFormData = z.infer<typeof enterpriseOrgNumbersSchema>
export type EnterpriseLeadSourcesFormData = z.infer<typeof enterpriseLeadSourcesSchema>
export type EnterpriseWorkflowsFormData = z.infer<typeof enterpriseWorkflowsSchema>
export type EnterpriseFocusFormData = z.infer<typeof enterpriseFocusSchema>
export type EnterpriseCreateRequestData = z.infer<typeof enterpriseCreateRequestSchema>
